/**
 * fieldGuide — shared design tokens for the "field guide" surfaces.
 *
 * The explorer hero established this language (full-bleed photo, a heavy
 * display face over Work Sans, flat brand colour, squared map-label tags, contour
 * linework). Anything adopting it imports the palette from here rather than
 * re-declaring hexes, so the surfaces can't drift apart the way the regions
 * page had drifted from the home page — same brand, two different blues
 * (#0066CC vs #1a68d1), two different display faces, gradients on one and not
 * the other.
 *
 * Both brands render these pages, so the palette resolves per brand.
 */
import React from 'react';
import { BRAND } from './brand';

export interface FieldGuidePalette {
  ink: string;
  inkSoft: string;
  inkMute: string;
  paper: string;
  paper2: string;
  line: string;
  accent: string;
  accentDeep: string;
  accentLight: string;
  page: string;
}

/**
 * Camping Australia uses the values its Shopify theme already ships — the navy
 * nav ground and the #1a68d1 blue family from the mega menu — rather than
 * BRAND.colors.primary (#3b82f6), which is the generic Tailwind blue kept
 * around for tile/theme meta tags. EscapeTo uses its own ember-on-pine
 * identity straight off BRAND.colors.
 */
export const PALETTE: FieldGuidePalette =
  BRAND.key === 'escapeto'
    ? {
        ink: BRAND.colors.dark, // pine
        inkSoft: '#2f4a41',
        inkMute: '#638175',
        paper: '#f6f2ea',
        paper2: '#ebe4d7',
        line: 'rgba(18,49,41,.16)',
        accent: BRAND.colors.primary, // ember
        accentDeep: '#c2501f',
        accentLight: BRAND.colors.gold || '#EDB458',
        page: '#f9f6f0',
      }
    : {
        ink: '#16233a',
        inkSoft: '#3a4a63',
        inkMute: '#6b7a90',
        paper: '#f2f6fb',
        paper2: '#e6edf7',
        line: 'rgba(22,35,58,.15)',
        accent: '#1a68d1',
        accentDeep: '#1558b0',
        accentLight: '#7cc4f5',
        page: '#f5f8fc',
      };

/** The `--fg-*` custom properties, for injection at the top of a scoped block. */
/**
 * Display face, per brand. Camping Australia sets headlines in Poppins
 * (800 hero / 700 section / 600 sub-head): the owner rejected Fraunces as
 * "Claude-coded" in Oct 2026. EscapeTo keeps its Fraunces display unchanged.
 */
export const SERIF_DISPLAY = BRAND.key !== 'camping-australia';

interface DisplaySpec { w: number; ls: string; size?: string; lh?: number | string }

/** CSS declarations for a display heading: `sans` applies to Camping Australia, `serif` to EscapeTo. */
export function displayType(sans: DisplaySpec, serif: DisplaySpec): string {
  const t = SERIF_DISPLAY ? serif : sans;
  const family = SERIF_DISPLAY ? 'font-family:Fraunces,Georgia,serif;font-optical-sizing:auto;' : 'font-family:Poppins,sans-serif;';
  return family + `font-weight:${t.w};letter-spacing:${t.ls};`
    + (t.size ? `font-size:${t.size};` : '')
    + (t.lh !== undefined ? `line-height:${t.lh};` : '');
}

/** The accent word inside a display title: italic in the serif, upright in Poppins. */
export const DISPLAY_EM_CSS = SERIF_DISPLAY ? 'font-style:italic;font-weight:500' : 'font-style:normal;font-weight:inherit';

export const TOKENS_CSS = `  --fg-ink:${PALETTE.ink};
  --fg-ink-soft:${PALETTE.inkSoft};
  --fg-ink-mute:${PALETTE.inkMute};
  --fg-paper:${PALETTE.paper};
  --fg-paper-2:${PALETTE.paper2};
  --fg-line:${PALETTE.line};
  --fg-blue:${PALETTE.accent};
  --fg-blue-dk:${PALETTE.accentDeep};
  --fg-blue-lt:${PALETTE.accentLight};
  --fg-page:${PALETTE.page};`;

/**
 * Contour-map linework — concentric irregular rings that read as an elevation
 * map. The recurring decorative motif of these pages; it's what lets a hero
 * carry visual interest without a blurred glass panel.
 *
 * No hooks, so it renders in both server and client components.
 */
export const ContourMark: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 520 420"
    aria-hidden="true"
    focusable="false"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.1"
  >
    <g transform="translate(268 206)">
      {[1, 0.855, 0.715, 0.58, 0.45, 0.33, 0.22, 0.13].map((scale, i) => (
        <path
          key={scale}
          transform={`scale(${scale}) rotate(${i * 4})`}
          d="M0 -150 C 68 -150 128 -100 138 -38 C 148 28 104 104 32 138 C -38 170 -114 138 -138 74 C -162 10 -134 -84 -68 -132 C -46 -145 -22 -150 0 -150 Z"
        />
      ))}
    </g>
  </svg>
);

/** Fine grain, as a data-URI background. Keeps large flat areas from looking dead. */
export const GRAIN_DATA_URI =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='180' height='180' filter='url(%23n)'/%3E%3C/svg%3E\")";
